import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { inventoryApi } from '../../api/inventory-api-instance';
import { stockApi } from '../../api/stock-api';
import { stockUnitLabels } from '../../domain/fulfillment';
import { siteLabel } from '../../lib/site-label';

export const STOCK_QUERY_KEYS = {
  items: ['stock', 'items'] as const,
  locations: ['stock', 'locations'] as const,
  balances: ['stock', 'balances'] as const,
  movements: ['stock', 'movements'] as const,
};

/** Catalog, locations, balances and sites the stock screens show names from. */
export function useStockReferences() {
  const items = useQuery({
    queryKey: STOCK_QUERY_KEYS.items,
    queryFn: () => stockApi.listStockItems(true),
  });
  const locations = useQuery({
    queryKey: STOCK_QUERY_KEYS.locations,
    queryFn: () => stockApi.listWarehouseLocations(undefined, true),
  });
  const balances = useQuery({
    queryKey: STOCK_QUERY_KEYS.balances,
    queryFn: () => stockApi.listStockBalances(),
  });
  const sites = useQuery({ queryKey: ['sites'], queryFn: () => inventoryApi.listSites() });

  return useMemo(() => {
    const itemById = new Map((items.data ?? []).map((i) => [i.id, i]));
    const locationById = new Map((locations.data ?? []).map((l) => [l.id, l]));
    const siteById = new Map((sites.data ?? []).map((s) => [s.id, s]));
    const locationName = (id: string | undefined): string => {
      if (!id) return '—';
      const l = locationById.get(id);
      if (!l) return id;
      const parent = l.parentId ? locationById.get(l.parentId) : undefined;
      const name = l.displayName || l.code;
      return parent ? `${parent.displayName || parent.code} › ${name}` : name;
    };
    return {
      loading: items.isLoading || locations.isLoading || balances.isLoading,
      error: items.isError || locations.isError || balances.isError,
      refetch: () => {
        void items.refetch();
        void locations.refetch();
        void balances.refetch();
      },
      items: items.data ?? [],
      locations: locations.data ?? [],
      balances: balances.data ?? [],
      sites: sites.data ?? [],
      item: (id: string) => itemById.get(id),
      location: (id: string) => locationById.get(id),
      locationName,
      siteName: (id: string) => siteLabel(siteById.get(id), id),
      itemName: (id: string) => {
        const i = itemById.get(id);
        return i ? `${i.displayName} (${i.sku})` : id;
      },
      unit: (id: string) => {
        const i = itemById.get(id);
        return i ? stockUnitLabels[i.stockUnit] : '';
      },
    };
  }, [items, locations, balances, sites.data]);
}

export type StockReferences = ReturnType<typeof useStockReferences>;
